import { useMemo } from 'react';
import type { AnalysisResult } from '@/core/analysis/types';
import { TUNINGS, type FrettedInstrument, type Instrument } from '@/core/instruments/shapes';
import { chordVoicing } from '@/core/practice/events';
import { chordName, type DisplayOptions } from '@/core/theory/display';
import { mod } from '@/core/util/math';
import { fretsFor, PianoDiagram, useVoicingPrefs } from '@/features/chord-diagrams';
import { useTimeIndex } from '@/lib/useTimeIndex';
import styles from './NeckView.module.css';

interface NeckViewProps {
  result: AnalysisResult;
  options: DisplayOptions;
  instrument: Instrument;
  leftHanded: boolean;
  visualOffsetMs: number;
}

const FRETS = 12;
const FRET_WIDTH = 64;
const NUT = 44;
const ROW = 30;
const INLAYS = [3, 5, 7, 9];
/** Keyboard view range: C2 (MIDI 36) to B5, four octaves. */
const FIRST_KEY = 36;

/**
 * The whole neck or keyboard (F4.5): the current chord's notes on a full guitar, ukulele,
 * mandolin or bass neck, or a four-octave keyboard. Fingers slide to the next chord, and the
 * board pulses on every beat.
 */
export function NeckView({
  result,
  options,
  instrument,
  leftHanded,
  visualOffsetMs,
}: NeckViewProps) {
  const { segments, beatGrid } = result;
  const starts = useMemo(() => segments.map((s) => s.start), [segments]);
  const index = useTimeIndex(starts, visualOffsetMs);
  const beat = useTimeIndex(beatGrid.beats, visualOffsetMs);
  const prefs = useVoicingPrefs();
  const current = index >= 0 ? segments[index] : undefined;
  const chord = current?.chord ?? null;
  const name = current ? chordName(chord, options) : '–';

  return (
    <section aria-label={instrument === 'piano' ? 'Keyboard' : 'Fretboard'} className={styles.card}>
      <div className={styles.header}>
        <span className={styles.name} aria-live="polite" data-testid="neck-chord">
          {name}
        </span>
        {options.capo > 0 && instrument !== 'piano' && (
          <span className={styles.capo}>Capo {options.capo}</span>
        )}
      </div>
      {/* Focusable so keyboard users can scroll a neck wider than the window (WCAG 2.1.1). */}
      <div
        className={styles.scroll}
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- scrollable region
        tabIndex={0}
        role="group"
        aria-label={instrument === 'piano' ? 'Keyboard diagram' : 'Fretboard diagram'}
      >
        {instrument === 'piano' ? (
          <div className={styles.keyboard}>
            <span key={beat} className={styles.pulse} aria-hidden="true" />
            <PianoDiagram
              octaves={4}
              keys={chord ? chordVoicing(chord, options.transpose).map((m) => m - FIRST_KEY) : []}
            />
          </div>
        ) : (
          <Fretboard
            instrument={instrument}
            frets={chord ? fretsFor(chord, instrument, options, prefs) : null}
            rootPc={chord ? mod(chord.root + options.transpose - options.capo, 12) : null}
            leftHanded={leftHanded}
            beat={beat}
          />
        )}
      </div>
    </section>
  );
}

function Fretboard({
  instrument,
  frets,
  rootPc,
  leftHanded,
  beat,
}: {
  instrument: FrettedInstrument;
  frets: number[] | null;
  rootPc: number | null;
  leftHanded: boolean;
  beat: number;
}) {
  const tuning = TUNINGS[instrument];
  const strings = tuning.length;
  const width = NUT + FRETS * FRET_WIDTH;
  const height = strings * ROW;
  /** Row of a string: the lowest string at the bottom, as players see the neck. */
  const rowOf = (s: number) => (strings - 1 - s) * ROW + ROW / 2;

  return (
    <div
      className={styles.board}
      style={{ width, height }}
      data-left-handed={leftHanded || undefined}
      aria-hidden="true"
    >
      <span key={beat} className={styles.pulse} />
      <span className={styles.nut} style={{ left: NUT - 6, height }} />
      {Array.from({ length: FRETS }, (_, i) => (
        <span
          key={i}
          className={styles.fret}
          style={{ left: NUT + (i + 1) * FRET_WIDTH, height }}
        />
      ))}
      {[...INLAYS, 12].map((f) => (
        <span
          key={f}
          className={styles.inlay}
          data-double={f === 12 || undefined}
          style={{ left: NUT + (f - 0.5) * FRET_WIDTH, top: height / 2 }}
        />
      ))}
      {tuning.map((_, s) => (
        <span
          key={s}
          className={styles.string}
          style={{ top: rowOf(s), width, height: 1 + (strings - 1 - s) * 0.4 }}
        />
      ))}
      {tuning.map((open, s) => {
        const f = frets?.[s] ?? -1;
        const root = rootPc !== null && f >= 0 && mod(open + f, 12) === rootPc;
        const left = f > 0 ? NUT + (f - 0.5) * FRET_WIDTH : NUT / 2 - 4;
        return (
          <span
            key={s}
            className={styles.note}
            data-kind={f < 0 ? 'muted' : f === 0 ? 'open' : 'fretted'}
            data-root={root || undefined}
            style={{ left, top: rowOf(s) }}
          />
        );
      })}
      {Array.from({ length: FRETS }, (_, i) => (
        <span
          key={`n${i}`}
          className={styles.fretNumber}
          style={{ left: NUT + (i + 0.5) * FRET_WIDTH, top: height + 4 }}
        >
          {INLAYS.includes(i + 1) || i + 1 === 12 ? i + 1 : ''}
        </span>
      ))}
    </div>
  );
}
